/**
 * Direct Topic Callback System
 * 
 * This replaces the complex message-based approach in topicDecisionEngine
 * with simple direct function calls.
 * 
 * Instead of:
 * 1. Create fake message
 * 2. Call processUserMessage
 * 3. Parse message
 * 4. Call strategy
 * 
 * We now do:
 * 1. Direct strategy call with context
 */

import React from 'react';
import { getStrategyForRoleType } from './topicStrategies';
import { StrategyPhase } from './topicStrategies/baseStrategy';
import centralDiscoveryQueue from './centralDiscoveryQueue';

/**
 * Direct topic callback manager
 * Handles topic operations without fake messages
 */
export class DirectTopicCallback {
  constructor({
    currentDiscoveryState,
    advanceToNextTopic,
    updateRequirements,
    getAllRequirements,
    handleRefreshResults,
    queries,
    sessionId,
    userId,
    setTitleKeys,
    setConfirmedSkills,
    discoveryProgress,
    chatInterfaceRef = null,
    onSetTyping = null // Add typing callback
  }) {
    this.currentDiscoveryState = currentDiscoveryState;
    this.advanceToNextTopic = advanceToNextTopic;
    this.updateRequirements = updateRequirements;
    this.getAllRequirements = getAllRequirements;
    this.handleRefreshResults = handleRefreshResults;
    this.queries = queries;
    this.sessionId = sessionId;
    this.userId = userId;
    this.setTitleKeys = setTitleKeys;
    this.setConfirmedSkills = setConfirmedSkills;
    this.discoveryProgress = discoveryProgress;
    this.chatInterfaceRef = chatInterfaceRef;
    this.onSetTyping = onSetTyping;
    this.populationLocks = new Map(); // Add locks to prevent simultaneous population
  }

  /**
   * Initialize current topic by calling its strategy directly
   * This replaces the need for fake messages
   */
  async initializeCurrentTopic() {
    const currentTopic = this.currentDiscoveryState?.currentDiscoTopic;
    const roleType = this.currentDiscoveryState?.roleType;

    if (!currentTopic || !roleType) {
      console.log('🎯 DIRECT CALLBACK - No current topic or role type');
      return false;
    }

    console.log('🎯 DIRECT CALLBACK - Initializing topic:', currentTopic, roleType);
    
    return await this.callTopicStrategy(currentTopic, roleType, StrategyPhase.INIT);
  }

  /**
   * Advance to next topic and initialize it
   * This replaces the message-based topic advancement
   */
  async advanceToNextTopicDirect() {
    console.log('🎯 DIRECT CALLBACK - Advancing to next topic');
    
    // Check if current topic is complete
    const currentTopic = this.currentDiscoveryState?.currentDiscoTopic;
    console.log('🎯 DIRECT CALLBACK - Current topic check:', {
      currentTopic,
      hasCurrentDiscoveryState: !!this.currentDiscoveryState,
      discoveryStateDetails: this.currentDiscoveryState ? {
        roleType: this.currentDiscoveryState.roleType,
        discoveryOrder: this.currentDiscoveryState.discoveryOrder,
        completedTopics: this.currentDiscoveryState.completedTopics
      } : null
    });
    
    if (currentTopic) {
      const isComplete = this.isTopicComplete(currentTopic);
      const topicItems = centralDiscoveryQueue.getItemsByTopic(currentTopic);
      console.log('🎯 DIRECT CALLBACK - Topic completion check:', {
        currentTopic,
        isComplete,
        totalItems: topicItems.length,
        completedItems: topicItems.filter(item => item.disco_done).length,
        pendingItems: topicItems.filter(item => !item.disco_done && !item.skipped).length
      });
      
      if (!isComplete) {
        console.log('🚨 DIRECT CALLBACK - Current topic not complete, cannot advance');
        return false;
      }
    }

    // Advance using the existing function
    console.log('🎯 DIRECT CALLBACK - Calling advanceToNextTopic function...');
    const updatedState = this.advanceToNextTopic();
    
    console.log('🎯 DIRECT CALLBACK - advanceToNextTopic result:', {
      hasUpdatedState: !!updatedState,
      updatedStateDetails: updatedState ? {
        currentDiscoTopic: updatedState.currentDiscoTopic,
        roleType: updatedState.roleType,
        completedTopics: updatedState.completedTopics,
        discoveryOrder: updatedState.discoveryOrder
      } : null
    });
    
    if (updatedState && updatedState.currentDiscoTopic) {
      // Initialize the new topic
      const newTopic = updatedState.currentDiscoTopic;
      const roleType = updatedState.roleType;
      
      console.log('🎯 DIRECT CALLBACK - Advanced to new topic:', {
        newTopic,
        roleType,
        previousTopic: currentTopic,
        completedTopics: updatedState.completedTopics
      });
      
      // Update our internal state
      this.currentDiscoveryState = updatedState;
      
      // Call strategy for the new topic to populate its queue
      console.log('🎯 DIRECT CALLBACK - Calling strategy for new topic:', newTopic);
      const startTime = Date.now();
      const strategyResult = await this.callTopicStrategy(newTopic, roleType, StrategyPhase.INIT);
      const endTime = Date.now();
      console.log('🎯 DIRECT CALLBACK - Strategy result for new topic:', {
        topic: newTopic,
        strategyResult,
        timeTaken: `${endTime - startTime}ms`
      });
      
      return strategyResult;
    } else {
      console.log('🎯 DIRECT CALLBACK - All topics completed or no valid next topic!');
      return { allComplete: true };
    }
  }

  /**
   * Call a topic strategy directly with proper context
   * This replaces processUserMessage logic
   */
  async callTopicStrategy(topic, roleType, phase = StrategyPhase.INIT, additionalContext = {}) {
    console.log('🎯 DIRECT CALLBACK - Calling strategy:', topic, roleType, phase);
    
    // CRITICAL: Check if topic already has items BEFORE calling strategy to prevent duplicates
    const allQueueItems = centralDiscoveryQueue.getAllItems();
    const existingTopicItems = allQueueItems.filter(item => item.topic === topic);
    
    // For company attribute topics in fresh start mode, allow showing existing items to user
    const isCompanyAttributeTopic = ['company_size', 'business_model', 'company_stage'].includes(topic);
    const isFreshStart = !this.getAllRequirements || 
      (this.getAllRequirements && (!this.getAllRequirements() || this.getAllRequirements().length === 0));
    
    if (existingTopicItems.length > 0 && phase === StrategyPhase.INIT) {
      // Allow company attribute topics to proceed in fresh start mode for user selection
      if (isCompanyAttributeTopic && isFreshStart) {
        console.log('✅ CALL TOPIC STRATEGY: Company attribute topic', topic, 'in fresh start mode - allowing strategy to proceed for user selection regardless of existing item status');
        // Always allow company attribute topics to run in fresh start mode
        // This ensures user interaction items are created even if there are completed seeded items
        // Continue to strategy call instead of skipping
      } else {
        console.log('🚫 CALL TOPIC STRATEGY: Topic', topic, 'already has', existingTopicItems.length, 'items, SKIPPING strategy call to prevent duplicates:', {
          existingItems: existingTopicItems.map(item => ({
            section_id: item.section_id,
            section_name: item.section_name,
            disco_done: item.disco_done,
            skipped: item.skipped
          }))
        });
        return { actionType: 'SKIPPED_DUPLICATE_PREVENTION', itemCount: existingTopicItems.length };
      }
    }
    
    try {
      // Get the strategy for this topic
      console.log('🎯 CALL TOPIC STRATEGY - Getting strategy for:', { topic, roleType });
      const strategy = getStrategyForRoleType(topic, roleType);
      console.log('🎯 CALL TOPIC STRATEGY - Strategy found:', {
        topic,
        hasStrategy: !!strategy,
        strategyName: strategy?.name || 'unnamed'
      });
      
      if (!strategy) {
        console.warn('🚨 DIRECT CALLBACK - No strategy found for:', topic, roleType, '- attempting auto-advance to next supported topic');
        // Attempt to auto-advance to the next topic that has a strategy
        try {
          // Prevent infinite loops across many unsupported topics
          const fallbackDepth = (additionalContext && typeof additionalContext.fallbackDepth === 'number')
            ? additionalContext.fallbackDepth
            : 0;
          if (fallbackDepth > 10) {
            console.warn('🚨 DIRECT CALLBACK - Fallback depth exceeded, aborting auto-advance loop');
            return { actionType: 'SKIPPED_NO_STRATEGY_MAX_DEPTH', skippedTopic: topic };
          }

          const updatedState = this.advanceToNextTopic && typeof this.advanceToNextTopic === 'function'
            ? this.advanceToNextTopic()
            : null;

          if (updatedState && updatedState.currentDiscoTopic && updatedState.currentDiscoTopic !== topic) {
            // Update internal state and try to initialize the new topic
            this.currentDiscoveryState = updatedState;
            console.log('🎯 DIRECT CALLBACK - Auto-advanced to:', updatedState.currentDiscoTopic, 'from unsupported topic:', topic);
            return await this.callTopicStrategy(
              updatedState.currentDiscoTopic,
              updatedState.roleType,
              StrategyPhase.INIT,
              { ...(additionalContext || {}), fallbackDepth: fallbackDepth + 1 }
            );
          }
          
          // If we couldn't advance, return a no-op result
          return { actionType: 'SKIPPED_NO_STRATEGY', skippedTopic: topic };
        } catch (advErr) {
          console.error('❌ DIRECT CALLBACK - Auto-advance after missing strategy failed:', advErr);
          return { actionType: 'SKIPPED_NO_STRATEGY_ERROR', skippedTopic: topic, error: String(advErr?.message || advErr) };
        }
      }

      // Create decision object with direct callbacks (no fake messages)
      console.log('🔍 DirectTopicCallback creating decision for topic:', topic, 'with sessionId:', this.sessionId);
      const decision = {
        actionType: 'DIRECT_STRATEGY_CALL',
        currentTopic: topic,
        strategyPhase: phase,
        updatedDiscoveryState: this.currentDiscoveryState,
        queries: this.queries,
        sessionId: this.sessionId,
        userId: this.userId,
        
        // Direct callbacks instead of message-based
        advanceToNextTopic: this.advanceToNextTopic,
        updateRequirements: this.updateRequirements,
        getAllRequirements: this.getAllRequirements,
        handleRefreshResults: this.handleRefreshResults,
        setTitleKeys: this.setTitleKeys,
        setConfirmedSkills: this.setConfirmedSkills,
        discoveryProgress: this.discoveryProgress,
        
        // Direct completion callback
        onTopicComplete: () => this.handleTopicComplete(topic),
        onQueuePopulated: (itemCount) => this.handleQueuePopulated(topic, itemCount),
        
        // Remove message-based callbacks
        onSendText: null,
        setMessages: null,
        
        // Additional context
        ...additionalContext
      };

      // Show typing indicator before calling strategy
      if (this.onSetTyping) {
        console.log('🧠 DIRECT CALLBACK - Setting typing indicator for strategy:', topic);
        this.onSetTyping(true, `Analyzing ${topic} requirements and preparing options...`);
      } else {
        console.log('❌ DIRECT CALLBACK - onSetTyping is null/undefined for strategy:', topic);
      }
      
      // Call strategy directly (no fake messages array)
      console.log('🎯 DIRECT CALLBACK - Executing strategy for:', topic);
      
      // Different strategies expect different third parameters:
      // - companyQueueStrategy expects queries as third parameter
      // - Most other strategies expect setMessages as third parameter
      // - Some queue strategies only expect (decision, messages)
      let thirdParam = null;
      if (topic === 'companies') {
        // companyQueueStrategy expects queries
        thirdParam = this.queries;
      } else {
        // Other strategies expect setMessages, but we don't have it in DirectCallback
        // Pass a no-op function to prevent errors
        thirdParam = () => {};
      }
      
      const result = await strategy(decision, [], thirdParam);
      
      // Clear typing indicator after strategy completes
      if (this.onSetTyping) {
        console.log('🧠 DIRECT CALLBACK - Clearing typing indicator for strategy:', topic);
        this.onSetTyping(false, '');
      }
      
      console.log('🎯 DIRECT CALLBACK - Strategy result:', result?.actionType || 'No result');
      
      return result;
    } catch (error) {
      console.error('❌ DIRECT CALLBACK - Strategy error:', error);
      
      // Clear typing indicator on error
      if (this.onSetTyping) {
        console.log('🧠 DIRECT CALLBACK - Clearing typing indicator due to error');
        this.onSetTyping(false, '');
      }
      
      return false;
    }
  }

  /**
   * Check if a topic is complete (all queue items done)
   */
  isTopicComplete(topic) {
    const items = centralDiscoveryQueue.getItemsByTopic(topic);
    const pendingItems = items.filter(item => !item.disco_done && !item.skipped);
    
    console.log('🎯 DIRECT CALLBACK - Topic completion check:', {
      topic,
      totalItems: items.length,
      pendingItems: pendingItems.length,
      completedItems: items.filter(item => item.disco_done).length,
      skippedItems: items.filter(item => item.skipped).length,
      isComplete: pendingItems.length === 0
    });
    
    return pendingItems.length === 0;
  }

  /**
   * Handle topic completion
   */
  handleTopicComplete(topic) {
    console.log('🎯 DIRECT CALLBACK - Topic completed:', topic);
    
    // Advance to next topic automatically
    setTimeout(() => {
      this.advanceToNextTopicDirect();
    }, 500);
  }

  /**
   * Handle queue population completion
   */
  handleQueuePopulated(topic, itemCount) {
    console.log('🎯 DIRECT CALLBACK - Queue populated:', topic, 'items:', itemCount);
    
    // Notify ChatInterface to re-render if needed
    if (this.chatInterfaceRef?.current?.forceUpdate) {
      this.chatInterfaceRef.current.forceUpdate();
    }
  }

  /**
   * Force topic strategy to populate queue items
   * This is used when we want to ensure queue is populated
   * If items already exist for the topic, they will be cleared and repopulated
   * However, if the topic has completed items, we skip repopulation to avoid rediscovering completed items
   */
  async populateTopicQueue(topic = null, roleType = null) {
    const targetTopic = topic || this.currentDiscoveryState?.currentDiscoTopic;
    const targetRoleType = roleType || this.currentDiscoveryState?.roleType;
    
    if (!targetTopic || !targetRoleType) {
      return false;
    }

    // Check if this topic is already being populated
    const lockKey = `${targetTopic}_${targetRoleType}`;
    if (this.populationLocks.has(lockKey)) {
      console.log('🔒 POPULATE QUEUE: Topic', targetTopic, 'is already being populated, waiting for completion...');
      return this.populationLocks.get(lockKey);
    }

    // Create a lock promise for this topic
    const populationPromise = this._doPopulateTopicQueue(targetTopic, targetRoleType);
    this.populationLocks.set(lockKey, populationPromise);
    
    try {
      const result = await populationPromise;
      return result;
    } finally {
      // Always remove the lock when done
      this.populationLocks.delete(lockKey);
    }
  }

  async _doPopulateTopicQueue(targetTopic, targetRoleType) {

    // Force reload from database to ensure we have the latest data
    console.log('🔄 POPULATE QUEUE: Ensuring queue is loaded by reloading from database...');
    if (this.sessionId) {
      try {
        await centralDiscoveryQueue.initialize(this.sessionId);
        console.log('✅ POPULATE QUEUE: Queue reloaded successfully');
      } catch (error) {
        console.error('❌ POPULATE QUEUE: Error reloading queue:', error);
      }
    }

    // Now check if topic already has ANY items (completed, pending, or skipped)
    const allQueueItems = centralDiscoveryQueue.getAllItems();
    const existingTopicItems = allQueueItems.filter(item => item.topic === targetTopic);
    
    console.log('🔍 POPULATE QUEUE: Final check for existing items:', {
      targetTopic,
      allQueueItemsCount: allQueueItems.length,
      existingTopicItemsCount: existingTopicItems.length,
      allTopics: [...new Set(allQueueItems.map(item => item.topic))]
    });
    
    // Check if this is a company attribute topic in fresh start mode
    const isCompanyAttributeTopic = ['company_size', 'business_model', 'company_stage'].includes(targetTopic);
    const isFreshStart = !this.getAllRequirements || 
      (this.getAllRequirements && (!this.getAllRequirements() || this.getAllRequirements().length === 0));
    
    // If topic already has any items, don't call strategy function to avoid creating duplicates
    if (existingTopicItems.length > 0) {
      const completedItems = existingTopicItems.filter(item => item.disco_done === true);
      const pendingItems = existingTopicItems.filter(item => item.disco_done !== true && item.skipped !== true);
      const skippedItems = existingTopicItems.filter(item => item.skipped === true);
      
      // Allow company attribute topics to proceed in fresh start mode
      if (isCompanyAttributeTopic && isFreshStart) {
        console.log('✅ POPULATE QUEUE: Company attribute topic', targetTopic, 'in fresh start mode - allowing strategy to proceed for user selection regardless of existing item status');
        // Continue to strategy call
      } else {
        console.log('🚫 POPULATE QUEUE: Topic', targetTopic, 'already has', existingTopicItems.length, 'items, SKIPPING strategy call to avoid duplicates:', {
          totalItems: existingTopicItems.length,
          completedItems: completedItems.length,
          pendingItems: pendingItems.length,
          skippedItems: skippedItems.length,
          isCompanyAttributeTopic,
          isFreshStart
        });
        return true;
      }
    }
    
    console.log('🎯 POPULATE QUEUE: Populating fresh items for topic:', targetTopic);
    // Call strategy to populate with fresh items
    return await this.callTopicStrategy(targetTopic, targetRoleType, StrategyPhase.INIT, {
      forceQueuePopulation: true,
      clearExistingItems: true
    });
  }

  /**
   * Handle user message for current topic
   * This allows updating queue items when user types while viewing options
   */
  async handleUserMessageForCurrentTopic(messages) {
    const currentTopic = this.currentDiscoveryState?.currentDiscoTopic;
    const roleType = this.currentDiscoveryState?.roleType;
    
    if (!currentTopic || !roleType) {
      console.warn('🚨 DIRECT CALLBACK - No current topic/roleType for user message handling');
      return false;
    }
    
    console.log('💬 DIRECT CALLBACK - Handling user message for topic:', currentTopic, 'with', messages.length, 'messages');
    
    try {
      // Get the strategy for this topic
      const strategy = getStrategyForRoleType(currentTopic, roleType);
      
      if (!strategy) {
        console.warn('🚨 DIRECT CALLBACK - No strategy found for user message handling:', currentTopic, roleType);
        return false;
      }

      // Create decision object for user message handling
      const decision = {
        actionType: 'USER_MESSAGE_UPDATE',
        currentTopic: currentTopic,
        strategyPhase: StrategyPhase.CONVERSATION,
        updatedDiscoveryState: this.currentDiscoveryState,
        queries: this.queries,
        sessionId: this.sessionId,
        userId: this.userId,
        
        // Direct callbacks
        advanceToNextTopic: this.advanceToNextTopic,
        updateRequirements: this.updateRequirements,
        getAllRequirements: this.getAllRequirements,
        handleRefreshResults: this.handleRefreshResults,
        setTitleKeys: this.setTitleKeys,
        setConfirmedSkills: this.setConfirmedSkills,
        discoveryProgress: this.discoveryProgress,
        
        // Completion callbacks
        onTopicComplete: () => this.handleTopicComplete(currentTopic),
        onQueuePopulated: (itemCount) => this.handleQueuePopulated(currentTopic, itemCount),
        
        // Remove message-based callbacks
        onSendText: null,
        setMessages: null
      };

      // Show typing indicator before calling strategy with user messages
      if (this.onSetTyping) {
        console.log('🧠 DIRECT CALLBACK - Setting typing indicator for user message handling:', currentTopic);
        this.onSetTyping(true, `Processing your ${currentTopic} requirements and generating new options...`);
      }
      
      // Call strategy with user messages
      console.log('💬 DIRECT CALLBACK - Executing strategy with user messages for:', currentTopic);
      
      // Different strategies expect different third parameters:
      // - companyQueueStrategy expects queries as third parameter
      // - Most other strategies expect setMessages as third parameter
      let thirdParam = null;
      if (currentTopic === 'companies') {
        // companyQueueStrategy expects queries
        thirdParam = this.queries;
      } else {
        // Other strategies expect setMessages, but we don't have it in DirectCallback
        // Pass a no-op function to prevent errors
        thirdParam = () => {};
      }
      
      const result = await strategy(decision, messages, thirdParam);
      
      // Clear typing indicator after strategy completes
      if (this.onSetTyping) {
        console.log('🧠 DIRECT CALLBACK - Clearing typing indicator for user message handling');
        this.onSetTyping(false, '');
      }
      
      console.log('💬 DIRECT CALLBACK - User message handling result:', result);
      
      return result;
    } catch (error) {
      console.error('💬 DIRECT CALLBACK - Error handling user message for topic:', currentTopic, error);
      
      // Clear typing indicator on error
      if (this.onSetTyping) {
        console.log('🧠 DIRECT CALLBACK - Clearing typing indicator due to error in user message handling');
        this.onSetTyping(false, '');
      }
      
      return false;
    }
  }

  /**
   * Get current topic progress
   */
  getCurrentTopicProgress() {
    const currentTopic = this.currentDiscoveryState?.currentDiscoTopic;
    
    if (!currentTopic) {
      return { completed: 0, total: 0, items: [] };
    }

    const items = centralDiscoveryQueue.getItemsByTopic(currentTopic);
    const completed = items.filter(item => item.disco_done).length;
    
    return {
      completed,
      total: items.length,
      items,
      topic: currentTopic,
      isComplete: completed === items.length
    };
  }
}

/**
 * Hook to use direct topic callbacks in components
 */
export function useDirectTopicCallback({
  currentDiscoveryState,
  advanceToNextTopic,
  updateRequirements,
  getAllRequirements,
  handleRefreshResults,
  queries,
  sessionId,
  userId,
  setTitleKeys,
  setConfirmedSkills,
  discoveryProgress,
  chatInterfaceRef = null,
  onSetTyping = null
}) {
  const manager = React.useMemo(() => {
    return new DirectTopicCallback({
      currentDiscoveryState,
      advanceToNextTopic,
      updateRequirements,
      getAllRequirements,
      handleRefreshResults,
      queries,
      sessionId,
      userId,
      setTitleKeys,
      setConfirmedSkills,
      discoveryProgress,
      chatInterfaceRef,
      onSetTyping
    });
  }, [currentDiscoveryState, advanceToNextTopic, updateRequirements, getAllRequirements, handleRefreshResults, queries, sessionId, userId, setTitleKeys, setConfirmedSkills, discoveryProgress, chatInterfaceRef, onSetTyping]);

  return React.useMemo(() => ({
    initializeCurrentTopic: manager.initializeCurrentTopic.bind(manager),
    advanceToNextTopicDirect: manager.advanceToNextTopicDirect.bind(manager),
    callTopicStrategy: manager.callTopicStrategy.bind(manager),
    populateTopicQueue: manager.populateTopicQueue.bind(manager),
    handleUserMessageForCurrentTopic: manager.handleUserMessageForCurrentTopic.bind(manager),
    getCurrentTopicProgress: manager.getCurrentTopicProgress.bind(manager),
    isTopicComplete: manager.isTopicComplete.bind(manager)
  }), [manager]);
}

export default DirectTopicCallback;
